import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ModelSummary, User } from '@wml-insta/shared';
import { api } from '../api/client.js';
import { NameModal } from '../ui/NameModal.js';
import { StatusStrip } from '../ui/StatusStrip.js';
import { UpdatedNote } from '../ui/UpdatedNote.js';
import { firstLetter, formatNum } from '../lib/format.js';

export function ModelsPage({ me: _me }: { me: User }) {
  const [models, setModels] = useState<ModelSummary[]>([]);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);

  function load() {
    api<ModelSummary[]>('/api/models').then(setModels).catch(() => setModels([]));
  }
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return models;
    const needle = q.trim().toLowerCase();
    return models.filter((m) => m.name.toLowerCase().includes(needle));
  }, [models, q]);

  // Как на карточках телефонов: считаем только живые акки, архивные — в «Архиве».
  const activeCount = (m: ModelSummary) =>
    (m.byStatus['В работе'] ?? 0) + (m.byStatus['Прогрев'] ?? 0) + (m.byStatus['Хьюман'] ?? 0);

  return (
    <div className="page-inner">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.02em' }}>Модели</h1>
          <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4 }}>
            {models.length} моделей · показано {filtered.length}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            className="admin-input"
            placeholder="Поиск по имени…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 220 }}
          />
          <button className="btn-primary" style={{ width: 'auto', padding: '9px 18px' }} onClick={() => setAdding(true)}>
            + Добавить модель
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">Ничего не найдено.</div>
      ) : (
        <div className="entity-grid">
          {filtered.map((m) => (
            <Link key={m.id} to={`/model/${m.id}`} className="entity-card">
              <div className="entity-head">
                <div className="model-avatar">{firstLetter(m.name)}</div>
                <div style={{ minWidth: 0 }}>
                  <div className="entity-name">{m.name}</div>
                  <div className="entity-meta">
                    {activeCount(m)} акк. · {formatNum(m.views24h)} просм./24ч
                  </div>
                  <UpdatedNote at={m.lastParsedAt} />
                </div>
              </div>
              <StatusStrip byStatus={m.byStatus} hideArchived />
            </Link>
          ))}
        </div>
      )}

      {adding && (
        <NameModal
          title="Добавить модель"
          label="Имя модели"
          submitLabel="Создать"
          onClose={() => setAdding(false)}
          onSubmit={async (name) => {
            await api('/api/models', { method: 'POST', body: JSON.stringify({ name }) });
            load();
          }}
        />
      )}
    </div>
  );
}
